import React, { useState } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import ComboBox from 'Components/ComboBox'
import { Input } from 'Components/Input'
import { useForm } from 'react-hook-form'
import { makeGetRequest, makePostRequest } from 'utils/api'
import ControlPointOutlinedIcon from '@mui/icons-material/ControlPointOutlined'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

// initialValues/trigger/onCreated/onError : permettent de réutiliser ce même
// formulaire depuis une liste de valeurs externe (onglet Marché) en pré-remplissant
// les champs connus (nom, prix, secteur...) et en affichant un déclencheur sur
// mesure (icône de ligne) plutôt que le "+" par défaut du header Portfolio.
export default function CreateOpportunityForm({
  setOperations,
  initialValues,
  trigger,
  onCreated,
  onError,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [open, setOpen] = useState(false)
  const [types, setTypes] = useState([])
  const [file, setFile] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      value_change: '',
      description: '',
      industry: '',
      ...initialValues,
    },
  })

  const fetchTypes = async () => {
    const url = '/api/investment/investment-type/'
    const { data, status } = await makeGetRequest(url, {}, true)
    if (status === 200) {
      const filterTypes = []
      data.forEach((v) => {
        filterTypes.push({ id: v.id, label: `${v.name}` })
      })
      setTypes([...filterTypes])
    }
  }

  const handleClickOpen = () => {
    setOpen(true)
    fetchTypes()
  }

  const handleClose = () => {
    reset()
    setOpen(false)
    setFile(null)
    setSelectedType(null)
  }

  const onSubmit = async (data) => {
    const formData = new FormData()
    if (file != null) formData.append('image', file)
    formData.append('type_id', selectedType.id)
    formData.append('name', data.name)
    formData.append('description', data.description)
    formData.append('sector', data.sector)
    formData.append('industry', data.industry)
    formData.append('price', data.price)
    formData.append('target_price', data.target_price)
    formData.append('target_value', data.target_value)
    formData.append('value_change', data.value_change)
    // Métadonnées connues de la liste source (symbole, variation marché) mais
    // non éditables dans ce formulaire : transmises telles quelles si présentes.
    if (initialValues?.market_id) formData.append('market_id', initialValues.market_id)
    if (initialValues?.value_change_pct !== undefined) {
      formData.append('value_change_pct', initialValues.value_change_pct)
    }

    const response = await makePostRequest(
      '/api/investment/tracked-opportunitie-operations/',

      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      true,
    )
    if (response.status === 201) {
      if (typeof setOperations === 'function') {
        setOperations((operations) => ({
          ...operations,
          results: [...operations.results, response.data],
          count: operations.count + 1,
        }))
      }
      if (typeof onCreated === 'function') onCreated(response.data)
      handleClose()
    } else if (typeof onError === 'function') {
      onError(response)
    }
  }

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, {
          onClick: (event) => {
            if (typeof trigger.props.onClick === 'function') trigger.props.onClick(event)
            handleClickOpen()
          },
        })
      ) : (
        <ControlPointOutlinedIcon onClick={handleClickOpen} />
      )}

      <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose}>
        {/* LAP-733 (GE022) */}
        <DialogTitle sx={{ color: 'Black !important' }}>{t('investments.forms.opportunity.addTitle')}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)} encType="multipart/form-data">
          <DialogContent>
            <ComboBox
              handleAutoComplete={(e, j) => {
                setValue('Type', j)
                setSelectedType(j)
              }}
              label={t('investments.forms.opportunity.type')}
              data={types}
              name="type"
              required={true}
              selectedValue={selectedType}
            />

            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'text',
              }}
              rules={{ required: true }}
              name="name"
              label={t('investments.forms.common.title')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: false,
                size: 'small',
                type: 'text',
              }}
              rules={{ required: false }}
              name="description"
              label={t('investments.forms.common.description')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'text',
              }}
              rules={{ required: true }}
              name="sector"
              label={t('investments.forms.opportunity.sector')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: false,
                size: 'small',
                type: 'text',
              }}
              rules={{ required: false }}
              name="industry"
              label={t('investments.forms.opportunity.industry')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'number',
              }}
              rules={{ required: true }}
              name="price"
              label={t('investments.forms.common.price')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'number',
              }}
              rules={{ required: true }}
              name="target_price"
              label={t('investments.forms.opportunity.targetPrice')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: true,
                size: 'small',
                type: 'number',
              }}
              rules={{ required: true }}
              name="target_value"
              label={t('investments.forms.opportunity.targetValue')}
            />
            <Input
              control={control}
              inputProps={{
                variant: 'outlined',
                required: false,
                size: 'small',
                type: 'number',
              }}
              rules={{ required: false }}
              name="value_change"
              label={t('investments.forms.opportunity.valueChange')}
            />

            <Button
              variant="contained"
              component="label"
              sx={{
                color: 'black',
                background: 'none',
                '&:hover': {
                  backgroundColor: '#aeaeae',
                },
              }}
            >
              <input
                style={{ width: '100%' }}
                name="image"
                type="file"
                filename={file}
                required={false}
                // accept="application/pdf"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </Button>
          </DialogContent>
          <DialogActions>
            {/* LAP-733 (GE022) */}
            <Button type="submit">{t('investments.forms.common.add')}</Button>
            <Button onClick={handleClose}>{t('investments.forms.common.cancel')}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
